// Attendance policy → Report: extra columns (one value per student, shown
// after the standard columns) and header cells (one value for the whole
// report, shown above the table). Each is a name + a formula
// (src/lib/policyFormula.ts).
//
// Left: the fields, each checked as you type (same rules as the server).
// Right: what you can use — click a chip to put it into the formula you
// were last editing. Below: a live preview on made-up students.
import { useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Calculator, Plus, Sigma, Table2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    AGGREGATES, HEADER_VARIABLES, MAX_COLUMNS, MAX_HEADERS, ROW_FUNCTIONS, ROW_VARIABLES,
    type FieldFormat, type ReportField, computeReport, fieldProblems, formatValue, keyFromName,
} from "@/lib/policyFormula";

type Kind = "columns" | "headers";
type Props = {
    columns: ReportField[];
    headers: ReportField[];
    minimumPercent: number | null;
    slotsPerDay: number;
    onChange: (columns: ReportField[], headers: ReportField[]) => void;
};

// Shown in the trigger directly: Radix leaves it blank for a value of "0".
const FORMAT_LABEL: Record<FieldFormat, string> = { number: "Number", percent: "Percent", yesno: "Yes / No" };

const EXAMPLES: Record<Kind, { name: string; formula: string; format: FieldFormat; decimals: number }[]> = {
    columns: [
        { name: "Eligible", formula: "percent >= ifempty(minimum_percent, 75)", format: "yesno", decimals: 0 },
        { name: "Classes short", formula: "max(0, ceil(sessions_on * ifempty(minimum_percent, 75) / 100 - sessions_present))", format: "number", decimals: 0 },
        { name: "Day %", formula: "days_any_present / days_on * 100", format: "percent", decimals: 1 },
    ],
    headers: [
        { name: "Class average", formula: "avg(percent)", format: "percent", decimals: 1 },
        { name: "Below minimum", formula: "count(percent < ifempty(minimum_percent, 75))", format: "number", decimals: 0 },
        { name: "Sessions held", formula: "sessions_held", format: "number", decimals: 0 },
    ],
};

// Made-up students for the preview (the same every time).
function sampleStudents(minimum: number | null, slotsPerDay: number) {
    const people: [ string, number, number, number ][] = [
        [ "Aarav S.", 38, 1, 1 ], [ "Diya K.", 31, 5, 4 ], [ "Kabir M.", 27, 12, 1 ],
        [ "Meera P.", 40, 0, 0 ], [ "Rohan G.", 22, 15, 3 ], [ "Sara T.", 35, 3, 2 ],
    ];
    const held = 40, days = 20;
    return people.map(([ name, present, absent, leave ]) => ({
        name,
        sessions_on: held, sessions_present: present, sessions_absent: absent, sessions_leave: leave,
        days_on: days, days_full_present: Math.max(0, Math.round(present / 2) - 2), days_any_present: Math.min(days, Math.round(present / 2) + 1),
        slots_per_day: slotsPerDay, percent: Math.round(present / held * 1000) / 10, minimum_percent: minimum,
    }));
}

export function PolicyReportFields({ columns, headers, minimumPercent, slotsPerDay, onChange }: Props) {
    const problems = useMemo(() => fieldProblems(columns, headers), [ columns, headers ]);
    const students = useMemo(() => sampleStudents(minimumPercent, slotsPerDay), [ minimumPercent, slotsPerDay ]);
    const preview = useMemo(() => computeReport(columns, headers, students, {
        students: students.length, sessions_held: 40, days_held: 20, minimum_percent: minimumPercent,
    }), [ columns, headers, students, minimumPercent ]);

    // where a chip goes: the formula input last focused
    const [ focus, setFocus ] = useState<{ kind: Kind; index: number } | null>(null);
    const inputs = useRef<Record<string, HTMLInputElement | null>>({});

    const list = (kind: Kind) => (kind === "columns" ? columns : headers);
    const save = (kind: Kind, next: ReportField[]) => (kind === "columns" ? onChange(next, headers) : onChange(columns, next));
    const allKeys = () => new Set([ ...columns, ...headers ].map(f => f.key));

    const add = (kind: Kind, example?: (typeof EXAMPLES)["columns"][number]) => {
        const name = example?.name ?? "";
        const field: ReportField = {
            name, key: name ? keyFromName(name, allKeys()) : "",
            formula: example?.formula ?? "", format: example?.format ?? "number", decimals: example?.decimals ?? 0,
        };
        save(kind, [ ...list(kind), field ]);
        setFocus({ kind, index: list(kind).length });
    };
    const update = (kind: Kind, i: number, patch: Partial<ReportField>) => {
        const current = list(kind)[ i ];
        // the key follows the name until it is set by hand
        if (patch.name !== undefined && (current.key === "" || current.key === keyFromName(current.name, new Set([ ...allKeys() ].filter(k => k !== current.key))))) {
            const others = new Set([ ...allKeys() ].filter(k => k !== current.key));
            patch.key = patch.name.trim() ? keyFromName(patch.name, others) : "";
        }
        save(kind, list(kind).map((f, j) => (j === i ? { ...f, ...patch } : f)));
    };
    const remove = (kind: Kind, i: number) => { save(kind, list(kind).filter((_, j) => j !== i)); setFocus(null); };
    const move = (kind: Kind, i: number, by: -1 | 1) => {
        const next = [ ...list(kind) ];
        const j = i + by;
        if (j < 0 || j >= next.length) return;
        [ next[ i ], next[ j ] ] = [ next[ j ], next[ i ] ];
        save(kind, next);
    };

    const insert = (text: string) => {
        if (!focus) return;
        const field = list(focus.kind)[ focus.index ];
        if (!field) return;
        const el = inputs.current[ `${focus.kind}:${focus.index}` ];
        const start = el?.selectionStart ?? field.formula.length;
        const end = el?.selectionEnd ?? field.formula.length;
        const formula = field.formula.slice(0, start) + text + field.formula.slice(end);
        update(focus.kind, focus.index, { formula });
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            const caret = start + (text.endsWith("()") ? text.length - 1 : text.length);
            el.setSelectionRange(caret, caret);
        });
    };

    const fieldCard = (kind: Kind, f: ReportField, i: number) => {
        const problem = (kind === "columns" ? problems.columns : problems.headers)[ i ];
        const total = list(kind).length;
        return (
            <div key={i} className={`rounded-lg border bg-card p-3 ${focus?.kind === kind && focus.index === i ? "ring-2 ring-primary/30" : ""}`}>
                <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-40 flex-1 space-y-1">
                        <Label className="text-[11px] text-muted-foreground">Name</Label>
                        <Input className="h-8" value={f.name} maxLength={40} placeholder={kind === "columns" ? "e.g. Eligible" : "e.g. Class average"}
                            onChange={(e) => update(kind, i, { name: e.target.value })} />
                    </div>
                    <div className="w-40 space-y-1">
                        <Label className="text-[11px] text-muted-foreground">Key (used in formulas)</Label>
                        <Input className="h-8 font-mono text-xs" value={f.key} maxLength={30}
                            onChange={(e) => update(kind, i, { key: e.target.value.toLowerCase() })} />
                    </div>
                    <div className="w-32 space-y-1">
                        <Label className="text-[11px] text-muted-foreground">Show as</Label>
                        <Select value={f.format} onValueChange={(v) => update(kind, i, { format: v as FieldFormat })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue>{FORMAT_LABEL[ f.format ]}</SelectValue></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="percent">Percent</SelectItem>
                                <SelectItem value="yesno">Yes / No</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {f.format !== "yesno" && (
                        <div className="w-24 space-y-1">
                            <Label className="text-[11px] text-muted-foreground">Decimals</Label>
                            <Select value={String(f.decimals)} onValueChange={(v) => update(kind, i, { decimals: Number(v) })}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue>{f.decimals}</SelectValue></SelectTrigger>
                                <SelectContent>{[ 0, 1, 2, 3, 4 ].map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="flex items-center">
                        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => move(kind, i, -1)} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === total - 1} onClick={() => move(kind, i, 1)} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(kind, i)} aria-label="Remove"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                </div>
                <div className="mt-2 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Formula</Label>
                    <Input
                        ref={(el) => { inputs.current[ `${kind}:${i}` ] = el; }}
                        className={`h-9 font-mono text-sm ${problem && f.formula ? "border-red-300 focus-visible:ring-red-200" : ""}`}
                        value={f.formula}
                        placeholder={kind === "columns" ? "e.g. sessions_present / sessions_on * 100" : "e.g. avg(percent)"}
                        onFocus={() => setFocus({ kind, index: i })}
                        onChange={(e) => update(kind, i, { formula: e.target.value })}
                        spellCheck={false}
                    />
                    {problem
                        ? <p className="text-xs text-red-600">{problem}</p>
                        : <p className="text-xs text-emerald-700">Looks good.</p>}
                </div>
            </div>
        );
    };

    const section = (kind: Kind) => {
        const items = list(kind);
        const max = kind === "columns" ? MAX_COLUMNS : MAX_HEADERS;
        return (
            <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h4 className="flex items-center gap-2 text-sm font-semibold">
                            {kind === "columns" ? <Table2 className="h-4 w-4 text-primary" /> : <Sigma className="h-4 w-4 text-primary" />}
                            {kind === "columns" ? "Extra columns" : "Header cells"}
                            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">{items.length}/{max}</Badge>
                        </h4>
                        <p className="text-xs text-muted-foreground">
                            {kind === "columns"
                                ? "One value per student, after the standard columns. Later columns can use earlier ones by key."
                                : "One value for the whole report, above the table. Use sum, avg, count, minof or maxof over students."}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {EXAMPLES[ kind ].map(ex => (
                            <Button key={ex.name} size="sm" variant="outline" className="h-7 text-xs" disabled={items.length >= max}
                                onClick={() => add(kind, ex)}>+ {ex.name}</Button>
                        ))}
                        <Button size="sm" className="h-7 gap-1 text-xs" disabled={items.length >= max} onClick={() => add(kind)}>
                            <Plus className="h-3.5 w-3.5" /> Blank
                        </Button>
                    </div>
                </div>
                {items.length === 0
                    ? <p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">None yet. Start from an example or a blank one.</p>
                    : <div className="space-y-2">{items.map((f, i) => fieldCard(kind, f, i))}</div>}
            </section>
        );
    };

    const chip = (text: string, help: string, insertText = text) => (
        <Tooltip key={text}>
            <TooltipTrigger asChild>
                <button type="button" disabled={!focus} onClick={() => insert(insertText)}
                    className="rounded border bg-card px-1.5 py-0.5 font-mono text-[11px] hover:border-primary hover:text-primary disabled:opacity-50">
                    {text}
                </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-64 text-xs">{help}</TooltipContent>
        </Tooltip>
    );
    const inHeader = focus?.kind === "headers";

    return (
        <TooltipProvider delayDuration={200}>
            <div className="grid gap-6 xl:grid-cols-[1fr_280px]">
                <div className="space-y-8">
                    {section("columns")}
                    {section("headers")}
                </div>

                {/* WHAT YOU CAN USE */}
                <aside className="h-fit space-y-4 rounded-lg border bg-muted/20 p-3 xl:sticky xl:top-0">
                    <p className="flex items-center gap-2 text-xs font-semibold"><Calculator className="h-4 w-4 text-primary" /> Insert into the formula</p>
                    {!focus && <p className="text-xs text-muted-foreground">Click into a formula first.</p>}
                    <div className="space-y-1.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{inHeader ? "Report totals" : "Per student"}</p>
                        <div className="flex flex-wrap gap-1">
                            {Object.entries(inHeader ? HEADER_VARIABLES : ROW_VARIABLES).map(([ k, help ]) => chip(k, help))}
                        </div>
                    </div>
                    {inHeader && (
                        <div className="space-y-1.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Over all students</p>
                            <div className="flex flex-wrap gap-1">{Object.entries(AGGREGATES).map(([ k, help ]) => chip(`${k}()`, help))}</div>
                            <p className="text-[11px] text-muted-foreground">Inside them: any per-student name or extra column.</p>
                        </div>
                    )}
                    {(inHeader ? columns.length > 0 : focus && focus.index > 0) && (
                        <div className="space-y-1.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{inHeader ? "Extra columns" : "Earlier columns"}</p>
                            <div className="flex flex-wrap gap-1">
                                {(inHeader ? columns : columns.slice(0, focus?.index ?? 0)).filter(c => c.key).map(c => chip(c.key, c.name || c.key))}
                            </div>
                        </div>
                    )}
                    <div className="space-y-1.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Functions</p>
                        <div className="flex flex-wrap gap-1">{Object.entries(ROW_FUNCTIONS).map(([ k, [ , , help ] ]) => chip(`${k}()`, help))}</div>
                    </div>
                    <div className="space-y-1.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Operators</p>
                        <div className="flex flex-wrap gap-1">
                            {[ [ "+", "add" ], [ "-", "subtract" ], [ "*", "multiply" ], [ "/", "divide (by 0 gives empty)" ],
                               [ ">=", "at least (1 or 0)" ], [ "<", "below (1 or 0)" ], [ "=", "equal (1 or 0)" ] ].map(([ op, help ]) => chip(op, help, ` ${op} `))}
                        </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Empty values (no minimum, division by 0) show as “—”.</p>
                </aside>
            </div>

            {/* PREVIEW */}
            {(columns.length > 0 || headers.length > 0) && (
                <section className="mt-8 space-y-3">
                    <div>
                        <h4 className="text-sm font-semibold">Preview</h4>
                        <p className="text-xs text-muted-foreground">Made-up students: 40 sessions over 20 days{minimumPercent !== null ? `, minimum ${minimumPercent}%` : ", no minimum"}.</p>
                    </div>
                    {headers.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                            {headers.map((h, i) => (
                                <div key={i} className="rounded-lg border bg-card p-3">
                                    <p className="truncate text-[11px] text-muted-foreground">{h.name || h.key || "—"}</p>
                                    <p className="text-lg font-semibold">{problems.headers[ i ] ? "—" : formatValue(preview.headers[ h.key ], h.format, h.decimals)}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="overflow-x-auto rounded-lg border">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/30">
                                    <TableHead className="pl-4">Student</TableHead>
                                    <TableHead className="text-right">Present</TableHead>
                                    <TableHead className="text-right">Held</TableHead>
                                    <TableHead className="text-right">%</TableHead>
                                    {columns.map((c, i) => <TableHead key={i} className="text-right text-primary">{c.name || c.key || "—"}</TableHead>)}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {students.map((s, r) => (
                                    <TableRow key={s.name}>
                                        <TableCell className="pl-4 text-sm">{s.name}</TableCell>
                                        <TableCell className="text-right text-sm">{s.sessions_present}</TableCell>
                                        <TableCell className="text-right text-sm">{s.sessions_on}</TableCell>
                                        <TableCell className="text-right text-sm">{s.percent}%</TableCell>
                                        {columns.map((c, i) => (
                                            <TableCell key={i} className="text-right text-sm font-medium">
                                                {problems.columns[ i ] ? "—" : formatValue((preview.rows[ r ][ c.key ] as number | null) ?? null, c.format, c.decimals)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </section>
            )}
        </TooltipProvider>
    );
}
